import { type FC, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { Stack } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { type FavoritePlace } from 'invictus-sdk-typescript'

import AutocompleteAddFavorite from '@DS/components/navigation/search/autocomplete/atoms/autocompleteAddFavorite/AutocompleteAddFavorite'
import AutocompleteFavorite from '@DS/components/navigation/search/autocomplete/atoms/autocompleteFavorite/AutocompleteFavorite'
import { svgLazyLoader } from '@Utils/icons'

import * as styles from './styles'

const HomeIcon = svgLazyLoader('colorIcon/maison')
const WorkIcon = svgLazyLoader('colorIcon/travail')
const OtherIcon = svgLazyLoader('mobility/geolocation_full')

export type FavoritePlaces = {
  homeFavoritePlace?: FavoritePlace
  workFavoritePlace?: FavoritePlace
  otherFavoritePlaces?: FavoritePlace[]
}

export type AutocompleteFavoritePlacesProps = {
  favoritePlaces: FavoritePlaces
  hasReachedMaximumPlaces: boolean
  onSelect(favoritePlace: FavoritePlace): void
  onClickOnAddFavoritePlace?: VoidFunction
}

const AutocompleteFavoritePlaces: FC<AutocompleteFavoritePlacesProps> = ({
  favoritePlaces: { homeFavoritePlace, workFavoritePlace, otherFavoritePlaces },
  hasReachedMaximumPlaces,
  onSelect,
  onClickOnAddFavoritePlace,
}) => {
  const theme = useTheme()

  const handleClickOnAddFavoritePlace = useCallback(() => {
    onClickOnAddFavoritePlace?.()
  }, [onClickOnAddFavoritePlace])

  const { formatMessage } = useIntl()

  return (
    <Stack gap={1} flexWrap="wrap" flexDirection="row" padding={theme.spacing('0', 2, 2)}>
      {homeFavoritePlace && (
        <AutocompleteFavorite
          label={homeFavoritePlace.label || formatMessage({ id: 'favoritePlaces_home_label' })}
          place={homeFavoritePlace.place.label}
          icon={HomeIcon}
          css={styles.card}
          elevation="elevated"
          onClick={() => onSelect(homeFavoritePlace)}
        />
      )}
      {workFavoritePlace && (
        <AutocompleteFavorite
          label={workFavoritePlace.label || formatMessage({ id: 'favoritePlaces_work_label' })}
          place={workFavoritePlace.place.label}
          icon={WorkIcon}
          css={styles.card}
          elevation="elevated"
          onClick={() => onSelect(workFavoritePlace)}
        />
      )}
      {otherFavoritePlaces?.map((favoritePlace) => {
        const {
          label,
          place: { id, label: placeLabel },
        } = favoritePlace

        return (
          <AutocompleteFavorite
            label={label}
            place={placeLabel}
            key={id}
            icon={OtherIcon}
            css={styles.card}
            elevation="elevated"
            onClick={() => onSelect(favoritePlace)}
          />
        )
      })}
      {!homeFavoritePlace && (
        <AutocompleteAddFavorite
          onClick={handleClickOnAddFavoritePlace}
          title={formatMessage({ id: 'favoritePlaces_home_label' })}
          subtitle={formatMessage({ id: 'favoritePlaces_add_label' })}
          css={styles.card}
          elevation="elevated"
        />
      )}
      {!workFavoritePlace && (
        <AutocompleteAddFavorite
          onClick={handleClickOnAddFavoritePlace}
          title={formatMessage({ id: 'favoritePlaces_work_label' })}
          subtitle={formatMessage({ id: 'favoritePlaces_add_label' })}
          css={styles.card}
          elevation="elevated"
        />
      )}
      {!hasReachedMaximumPlaces && (
        <AutocompleteAddFavorite onClick={handleClickOnAddFavoritePlace} css={styles.card} elevation="elevated" />
      )}
    </Stack>
  )
}

export default AutocompleteFavoritePlaces
